
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { useIncrementPageView } from '@/common/apis/services/profile/incrementPageView';
import { usePageView } from '@/common/apis/services/profile/pageView';
import Modal from '@/common/components/atom/modal/modal';
import { LayoutWithHeaderAndFooter } from '@/common/components/layouts/layoutWithHeaderAndFooter';
import Seo from '@/common/components/layouts/seo';
import useApplication from '@/common/hooks/useApplication';
import useCustomize from '@/common/hooks/useCustomize';
import useModal from '@/common/hooks/useModal';
import useWebView from '@/common/hooks/useWebView';
import { splunkInstance } from '@/common/services/splunk';
import { buildDoctorExpertiseChips } from '@/common/utils/buildDoctorExpertiseChips';
import { getDoctorKeywordTags } from '@/common/apis/services/doctor/getDoctorKeywordTags';
import scrollIntoViewWithOffset from '@/common/utils/scrollIntoViewWithOffset';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { hasDoctorIdentity } from '@/common/utils/doctorIdentity';
import { useDoctorPageView } from '@/modules/profile/hooks/useDoctorPageView';
import { getProfileServerSideProps } from '@/modules/profile/functions/getProfileServerSideProps';
import { buildDoctorProfileJsonLds } from '@/modules/profile/functions/buildDoctorProfileJsonLds';
import { useProfileSplunkEvent } from '@/modules/profile/hooks/useProfileEvent';
import { useFeedbackDataStore } from '@/modules/profile/store/feedbackData';
import { useProfileDataStore } from '@/modules/profile/store/profileData';
import { Aside, areAsideChunksLoaded, preloadAsideChunks } from '@/modules/profile/views/aside';
import { sections } from '@/modules/profile/views/sections';
import { getCookie } from 'cookies-next';
import config from 'next/config';
import { ReactElement, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { growthbook } from '../_app';
import { isOnForSlug } from '@/common/growthbook/isOnForSlug';
import RaviGlobalContextsProvider from '../../../.plasmic/plasmic/ravi_r_r/PlasmicGlobalContextsProvider';
import ProfileGlobalContextsProvider from '../../../.plasmic/plasmic/paziresh_24_profile/PlasmicGlobalContextsProvider';
import { Fragment2 } from '@/common/fragment/fragment2';
import { useSearchStore } from '@/modules/search/store/search';
import useLockScroll from '@/common/hooks/useLockScroll';
import { useSlowNavigationOverlay } from '@/common/hooks/useSlowNavigationOverlay';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import ErrorPage from '@/modules/profile/components/errorPage';
import { sendDoctorProfileEvent } from '@/modules/profile/events/nightBatEvents';
import dynamic from 'next/dynamic';
import { useProfileHamdastWidgets } from '@/modules/profile/hooks/useProfileHamdastWidgets';
import { useDoctorBikoInsurance } from '@/modules/profile/hooks/useDoctorBikoInsurance';
import { useDoctorCenterFacilities } from '@/modules/profile/hooks/useDoctorCenterFacilities';
import {
  hasBikoInsuranceDisplayData,
  withoutHamdastBimehnamaWidgets,
} from '@/modules/profile/utils/bikoInsuranceProfile';
import { withoutHamdastCheraghWidgets } from '@/modules/profile/utils/cheraghFacilitiesProfile';
import type { CenterFacilitiesMap } from '@/modules/profile/utils/cheraghFacilitiesProfile';
import { formatAverageRate } from '@/modules/profile/utils/getAverageRate';
import { shouldShowProfileHeadRateBox } from '@/modules/profile/utils/shouldShowProfileHeadRateBox';
import { recordMicroserviceProfilePageView } from '@/modules/profile/utils/recordMicroserviceProfilePageView';
import {
  isPicUserImageUrl,
  resolveDoctorProfileImageUrl,
  resolveDoctorProfileLcpImageUrl,
} from '@/modules/profile/utils/doctorProfileImageUrl';
import { trackDoctorProfileViewByDoctorId } from '@/modules/hamdast/utils/profileViewFrequency';
import {
  clearProfileDoctorInfo,
  getProfileDoctorInfo,
  setProfileDoctorInfo,
  updateProfileDoctorInfo,
} from '@/modules/hamdast/stores/profileDoctorInfo';
import Loading from '@/common/components/atom/loading';
import { useRouter } from 'next/router';
import Script from 'next/script';
import Head from 'next/head';
import { MEMBERSHIP_NUMBER_LABEL } from '@/modules/profile/utils/buildAboutBiographyWithMedicalCode';
import { isPsychologist } from '@/modules/profile/utils/isPsychologist';
import { ProfileHead } from '@/modules/profile/components/ProfileHead';
import { useProfileBarImage } from '@/modules/profile/store/profileBarImage';
import { ProfileSectionTabs } from '@/modules/profile/components/ProfileSectionTabs';
import PlasmicRateAndCommentCount from '.plasmic/plasmic/ravi_r_r/PlasmicRateAndCommentCount';
import { useRate } from '@/common/apis/services/reviews/rate';
import { ProfileBackendDebugBadge } from '@/modules/profile/components/profileBackendDebugBadge';
import { useProgressiveProfile } from '@/modules/profile/hooks/useProgressiveProfile';
import { ProfileSectionList } from '@/modules/profile/components/ProfileSectionList';
import { BookingHintsContext, ProfileSectionSkeleton, RatePillSkeleton, SeedRate } from '@/modules/profile/components/ProfileSectionSkeleton';
import { useProgressiveHeadImage } from '@/modules/profile/hooks/useProgressiveHeadImage';
import { sectionReady, type SectionId } from '@/modules/profile/utils/sectionReadiness';
import { useSamanDoctorAvailabilityStatus } from '@/common/apis/services/booking/samanDoctorAvailabilityStatus';
import { useChatLifecycleAutoCloseHours } from '@/common/apis/services/hami/chatLifecycleSettings';
import { CENTERS } from '@/common/types/centers';
import {
  isProgressiveProfileShell,
  progressiveHeadArgs,
  revealSectionStates,
} from '@/modules/profile/utils/progressivePage';

const { publicRuntimeConfig } = config();

const Hamdast = dynamic(() => import('@/modules/hamdast/render').then(m => ({ default: m.Hamdast })), {
  ssr: false,
  loading: () => null,
});

/** Sections that render Saman availability and grow when it lands (held pending until it settles). */
const SAMAN_GATED: SectionId[] = ['booking', 'risman'];
/** Upper bound on that wait: a hanging Saman must not keep the booking card a skeleton. */
const SAMAN_REVEAL_TIMEOUT_MS = 2500;

/** Avatar src while the picture is unknown (the circle shimmers via [data-avatar-pending]). */
const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const SSR_ERROR_STATUSES = [404, 410, 500, 502, 503, 504];

const DoctorProfile = (props: any) => {
  const { shouldFetchOnClient, slug: initialSlug, status, errorMessage } = props;
  const isSsrErrorPage = SSR_ERROR_STATUSES.includes(Number(status));
  const { query, ...router } = useRouter();
  const [isProfileScriptsReady, setIsProfileScriptsReady] = useState(false);
  const profileScriptsReadyRef = useRef(false);
  const vicControlSentForSlugRef = useRef<string | null>(null);
  const trackedDoctorIdRef = useRef<string | null>(null);
  const revealedSectionsRef = useRef<{ slug: unknown; set: Set<string> }>({ slug: undefined, set: new Set() });

  const currentSlug = initialSlug ?? query.slug;

  useEffect(() => {
    if (profileScriptsReadyRef.current) return;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const enable = () => {
      if (profileScriptsReadyRef.current) return;
      profileScriptsReadyRef.current = true;
      setIsProfileScriptsReady(true);
    };
    const onInteract = () => {
      enable();
      cleanup();
    };
    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('pointerdown', onInteract);
      window.removeEventListener('keydown', onInteract);
      window.removeEventListener('touchstart', onInteract);
      window.removeEventListener('scroll', onInteract);
    };
    window.addEventListener('pointerdown', onInteract, { passive: true });
    window.addEventListener('keydown', onInteract, { passive: true });
    window.addEventListener('touchstart', onInteract, { passive: true });
    window.addEventListener('scroll', onInteract, { passive: true });
    timeoutId = setTimeout(enable, 10000);
    return cleanup;
  }, []);

  // Client-fetch shell: fetch the profile piece by piece and render each section as soon as it is ready.
  // MS 503/5xx SSR error pages must not hydrate from the browser (it cannot call PROFILE_API).
  const isClientShell = isProgressiveProfileShell({
    shouldFetchOnClient: !!shouldFetchOnClient,
    hasInformation: !!props?.information,
    isSsrErrorPage,
  });
  const progressive = useProgressiveProfile(currentSlug, isClientShell);
  // Booking/Risman render a 198px placeholder until Saman availability arrives and then grow
  // (measured: +245px after reveal → CLS). Hold them pending until it settles; same query key as
  // Services/Risman/SamanBooking, so this only starts the shared request earlier.
  const samanForReveal = useSamanDoctorAvailabilityStatus(isClientShell ? String(currentSlug ?? '') : '', {
    enabled: isClientShell && !!currentSlug,
  });
  // Bounded wait, counted from the moment booking's own data is ready (so a slow network that delays
  // everything does not use the budget up before booking could show anyway).
  const bookingDataReady = isClientShell && sectionReady('booking', progressive.pieces, progressive.seed);
  const [samanWaitExpired, setSamanWaitExpired] = useState(false);
  useEffect(() => {
    setSamanWaitExpired(false);
    if (!bookingDataReady) return;
    const t = setTimeout(() => setSamanWaitExpired(true), SAMAN_REVEAL_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [bookingDataReady, currentSlug]);
  const samanSettled = samanForReveal.isSuccess || samanForReveal.isError || samanWaitExpired;
  const [asideChunksReady, setAsideChunksReady] = useState(areAsideChunksLoaded);
  useEffect(() => {
    if (!isClientShell || asideChunksReady) return;
    let live = true;
    const done = () => live && setAsideChunksReady(true);
    preloadAsideChunks().then(done, done);
    return () => {
      live = false;
    };
  }, [isClientShell, asideChunksReady]);
  // Analytics/profile-data side effects wait for every piece (same count/payload as a full render).
  const progressivePending = isClientShell && !progressive.complete;
  const clientData = isClientShell ? { props: progressive.props } : undefined;

  const { customize } = useCustomize();
  const isApplication = useApplication();
  const isWebView = useWebView();
  const addPageView = usePageView();
  const incrementPageView = useIncrementPageView();
  const { recommendEvent } = useProfileSplunkEvent();
  const { handleOpen: handleOpenViewAsModal, modalProps: viewAsModalProps } = useModal();
  const [editable, setEditable] = useState(false);
  const [viewAdData, setViewAsData] = useState({ title: '', url: '' });
  const userInfo = useUserInfoStore(state => state.info);
  const userPending = useUserInfoStore(state => state.pending);
  const setProfileData = useProfileDataStore(state => state.setData);
  const setIsOpenSuggestion = useSearchStore(state => state.setIsOpenSuggestion);
  const { openScroll, lockScroll } = useLockScroll();
  const dontShowRateDetails = useFeatureIsOn('ravi_show_external_rate');
  const showHamdastGa = useFeatureIsOn('hamdast::ga');

  // Initial page props only (stable for this mount) — layout SEO may be a CSR shell.
  const layoutWasCsrFallback = !!props?.shouldFetchOnClient;

  const preferClientProps =
    !isSsrErrorPage && ((!!shouldFetchOnClient && !props?.information) || !!(clientData?.props as any)?.information);
  const finalProps = (preferClientProps && clientData?.props ? clientData.props : props) ?? props;

  const {
    slug,
    breadcrumbs,
    information,
    centers,
    media,
    symptomes,
    history,
    onlineVisit,
    similarLinks,
    similarDoctors,
    isBulk,
    expertises,
    feedbacks,
    waitingTimeInfo,
    shouldUseIncrementPageView,
    fragmentComponents,
    hamdastWidgets: hamdastWidgetsFromProps,
    hamdastWidgetsData: hamdastWidgetsDataFromProps,
    bikoInsurance: bikoInsuranceFromProps,
    centerFacilities: centerFacilitiesFromProps,
    keywordTags,
    user_id,
    dontShowRateAndReviewMessage,
    useSamanAvailabilityStatus: ssrUseSamanAvailabilityStatus,
    profile_backend,
    profile_api_id,
    profile_fallback_reason,
    profile_type,
    profile_perf,
    profile_debug_meta,
    shouldFetchOnClient: shouldFetchOnClientFromProps,
    title: documentTitleFromProps,
    description: documentDescriptionFromProps,
    host: documentHostFromProps,
  } = finalProps ?? {};

  const showProfileBackendDebug = query.profileDebug === '1' || query.profile_debug === '1' || query.rateDebug === '1';

  const doctorSlugForTags = String(slug ?? currentSlug ?? '');
  const { data: clientKeywordTags = [] } = useQuery(
    ['profileExpertiseKeywordTags', doctorSlugForTags],
    () =>
      getDoctorKeywordTags({
        slug: doctorSlugForTags,
        searchText: [information?.name, information?.family].filter(Boolean).join(' ') || undefined,
      }),
    {
      enabled:
        !!doctorSlugForTags && !(Array.isArray(keywordTags) && keywordTags.length > 0),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );
  const resolvedKeywordTags =
    (Array.isArray(keywordTags) && keywordTags.length > 0 ? keywordTags : clientKeywordTags) ?? [];

  // useLayoutEffect so slug is on GB before Services evaluates online-visit-chat-lifecycle-duration.
  useLayoutEffect(() => {
    if (!growthbook) return;
    const doctorSlugForGrowthbook = (typeof slug === 'string' && slug ? slug : null) ?? currentSlug;
    growthbook.setAttributes({
      ...growthbook.getAttributes(),
      // Prefer canonical profile slug from props when available (URL slug can omit suffix like `-3`).
      slug: doctorSlugForGrowthbook,
    });
    return () => {
      growthbook.setAttributes({
        ...growthbook.getAttributes(),
        slug: undefined,
      });
    };
  }, [currentSlug, slug, growthbook]);

  const rateDebug = query.rateDebug === '1';
  const rateDebugSlug = slug ?? currentSlug;
  const rateDebugProbe = useRate(
    { slug: String(rateDebugSlug ?? '') },
    { enabled: rateDebug && !!rateDebugSlug, refetchOnWindwFocus: false },
  );

  const doctorProfileImageUrl = useMemo(
    () => resolveDoctorProfileImageUrl(information, user_id),
    [information?.user_id, information?.image, user_id],
  );

  const doctorProfileLcpImageUrl = useMemo(
    () => resolveDoctorProfileLcpImageUrl(information, user_id),
    [information?.user_id, information?.image, user_id],
  );

  // Progressive header avatar: the search card's (cached) image from the first frame, a shimmering
  // circle while unknown — never the "noimage" fallback of a still-pending user id.
  const headImageUrl = useProgressiveHeadImage({
    enabled: isClientShell,
    seedImage: progressive.seed?.image,
    finalUrl: doctorProfileLcpImageUrl,
    complete: progressive.complete,
  });

  // The app bar's avatar follows the head's photo (the layout cannot see it on client navigations).
  const setBarImage = useProfileBarImage(state => state.setImage);
  useEffect(() => {
    setBarImage(headImageUrl || undefined);
  }, [headImageUrl, setBarImage]);
  useEffect(() => () => setBarImage(undefined), [setBarImage]);

  const clientSeoJsonLds = useMemo(
    () =>
      buildDoctorProfileJsonLds({
        slug,
        centers,
        information,
        expertises,
        feedbacks,
        breadcrumbs,
        doctorProfileImageUrl,
      }),
    [slug, centers, information, expertises, feedbacks, breadcrumbs, doctorProfileImageUrl],
  );

  const isOwnProfile = !!user_id && !!userInfo?.id && String(user_id) === String(userInfo?.id);
  const hamdastWidgetsQuery = useProfileHamdastWidgets(user_id, information?.id, isOwnProfile);
  const hamdastWidgetsRaw =
    isOwnProfile && hamdastWidgetsQuery.data ? hamdastWidgetsQuery.data.widgets : hamdastWidgetsFromProps;
  const hamdastWidgets = withoutHamdastCheraghWidgets(withoutHamdastBimehnamaWidgets(hamdastWidgetsRaw));
  const hamdastWidgetsData =
    isOwnProfile && hamdastWidgetsQuery.data ? hamdastWidgetsQuery.data.widgetsData : hamdastWidgetsDataFromProps;
  const bikoInsuranceQuery = useDoctorBikoInsurance(user_id, {
    enabled: !!user_id,
    initialData: hasBikoInsuranceDisplayData(bikoInsuranceFromProps) ? bikoInsuranceFromProps : null,
  });
  const bikoInsurance = hasBikoInsuranceDisplayData(bikoInsuranceQuery.data)
    ? bikoInsuranceQuery.data
    : hasBikoInsuranceDisplayData(bikoInsuranceFromProps)
    ? bikoInsuranceFromProps
    : null;
  const centerFacilitiesQuery = useDoctorCenterFacilities(user_id, centers, {
    enabled: !!user_id && Array.isArray(centers) && centers.length > 0,
    initialData: (centerFacilitiesFromProps as CenterFacilitiesMap | null) ?? null,
  });
  // Client shell: the online-visit card re-lays out (+12px) when Hami's chat-lifecycle days land,
  // which is after the booking section is revealed. Start that request here (same key as Services)
  // and keep booking pending until it settles, so the card appears once, at its final height.
  // Also when the flag targets this doctor's slug (Services turns it on that way too): otherwise the card
  // was revealed without the lifecycle line and grew when the hours arrived.
  const chatLifecycleFlagGlobal = useFeatureIsOn('online-visit-chat-lifecycle-duration');
  const chatLifecycleFlag =
    chatLifecycleFlagGlobal || isOnForSlug(growthbook, 'online-visit-chat-lifecycle-duration', String(currentSlug ?? ''));
  const lifecycleUserId = information?.user_id ?? user_id;
  const chatLifecycleForReveal = useChatLifecycleAutoCloseHours({
    enabled:
      isClientShell &&
      chatLifecycleFlag &&
      Array.isArray(centers) &&
      centers.some((c: any) => String(c?.id) === CENTERS.CONSULT),
    doctorUserId: lifecycleUserId,
    fallbackSlug: slug,
    // Same key as Services (doctor.user_id ?? profileData.user_id, else seo.slug) → shared cache, no extra request.
    doctorSlug: lifecycleUserId ? undefined : slug,
  });
  // Plasmic's GrowthbookGlobalContext overwrites `slug` with the viewer's own provider slug whenever a
  // Plasmic section mounts. On the progressive shell sections mount after the layout effect above
  // (legacy: under the spinner, before it), so restore it — a passive parent effect runs after the
  // children's. Section flags (useFeatureIsOn, e.g. ravi_show_external_rate, chat lifecycle) depend on it.
  // Deps: sections mount only on commits driven by the progressive data / reveal inputs
  // (progressive.props, samanSettled, asideChunksReady, chat-lifecycle pending), so re-running on
  // those covers every Plasmic mount; the render-time GB slug covers any later overwrite that
  // re-renders this page. Must not run for legacy SSR pages (isClientShell false).
  const growthbookSlugAttr = growthbook?.getAttributes()?.slug;
  useEffect(() => {
    if (!growthbook || !isClientShell) return;
    const doctorSlugForGrowthbook = (typeof slug === 'string' && slug ? slug : null) ?? currentSlug;
    if (growthbook.getAttributes().slug === doctorSlugForGrowthbook) return;
    growthbook.setAttributes({ ...growthbook.getAttributes(), slug: doctorSlugForGrowthbook });
  }, [
    growthbook,
    isClientShell,
    slug,
    currentSlug,
    growthbookSlugAttr,
    progressive.props,
    samanSettled,
    asideChunksReady,
    chatLifecycleForReveal.isPending,
  ]);
  const centerFacilities: CenterFacilitiesMap = {
    ...((centerFacilitiesFromProps as CenterFacilitiesMap | null) ?? {}),
    ...(centerFacilitiesQuery.data ?? {}),
  };

  useEffect(() => {
    if (progressivePending) return;
    useFeedbackDataStore.setState({ data: feedbacks?.feedbacks ?? [] });
  }, [feedbacks, progressivePending]);

  const isPageLoading = useSlowNavigationOverlay(isSearchOrBookingUrl);

  useEffect(() => {
    setIsOpenSuggestion(false);
    openScroll();
  }, [currentSlug]);

  // One page view per visit. ravi_show_external_rate is evaluated for this doctor's slug inside the
  // hook, so it no longer waits on the global GB slug attribute (growthbookGlobalContext rewrites it
  // when the user loads, which could drop the event).
  useDoctorPageView({
    growthbook,
    ready: !!growthbook?.ready,
    userPending,
    progressivePending,
    informationDep: isClientShell ? information?.id : information,
    input: information
      ? {
          information,
          centers,
          expertises,
          history,
          feedbacks,
          isBulk,
          isWebView: !!isWebView || !!isApplication,
          features: {
            risman: fragmentComponents?.risman && isBulk,
            risman_stale_dropped: !!fragmentComponents?.risman?.data?.destination?.['stale-dropped'],
          },
          viewer_user_id: userInfo?.id,
          enabledWidgets: hamdastWidgets?.map((item: any) => item?.id) ?? [],
          slug,
        }
      : null,
  });

  const renderResultSentRef = useRef<string | null>(null);
  useEffect(() => {
    if (progressivePending) return;
    if (!information?.id) return;
    const key = `${slug}:${information.id}`;
    if (renderResultSentRef.current === key) return;
    renderResultSentRef.current = key;
    sendDoctorProfileEvent('profile_render_result', 'success', {
      slug,
      doctor_id: information.id,
      should_fetch_on_client: !!shouldFetchOnClient,
      centers_count: centers?.length ?? 0,
      is_bulk: !!isBulk,
    });
  }, [information?.id, slug, shouldFetchOnClient, centers?.length, isBulk, progressivePending]);

  useEffect(() => {
    if (progressivePending) return;
    if (information) {
      if (shouldUseIncrementPageView) {
        incrementPageView.mutate({
          provider_id: information.provider_id,
        });
      }

      // New backend (microservice): profiles-api page-views only — never vicControl.
      // Legacy: keep clinic vicControl.
      if (vicControlSentForSlugRef.current !== slug) {
        vicControlSentForSlugRef.current = slug;
        if (profile_backend === 'microservice' && profile_api_id) {
          void recordMicroserviceProfilePageView({
            profileApiId: String(profile_api_id),
            viewerUserId: userInfo?.id,
          }).catch(() => {
            // Soft-fail: view count must not break profile
          });
        } else if (profile_backend !== 'microservice') {
          addPageView.mutate({
            ownerId: information.id,
            serverId: information.server_id,
            type: 'doctor',
          });
        }
      }

      const doctorUserId = information?.user_id ?? user_id ?? null;
      window.doctor = {
        ...information,
        user_id: doctorUserId,
        centers,
        expertises,
        isBulk,
        slug,
        history,
      };

      if (information.should_recommend_other_doctors) recommendEvent('loadrecommend');
      setProfileData({ ...information, centers: [...centers], ...expertises, feedbacks });
    }
  }, [
    isBulk,
    information,
    slug,
    userInfo,
    shouldUseIncrementPageView,
    centers,
    expertises,
    history,
    feedbacks,
    user_id,
    profile_backend,
    profile_api_id,
    progressivePending,
  ]);

  // Cache doctor identity for hamdast doctor-service payment (only clear when user_id/slug lifecycle ends).
  useEffect(() => {
    if (progressivePending) return;
    const doctorUserId = information?.user_id ?? user_id;
    if (!doctorUserId) return;

    const displayName =
      information?.display_name?.trim() ||
      [information?.name, information?.family].filter(Boolean).join(' ').trim() ||
      '';

    const epoch = setProfileDoctorInfo({
      userId: String(doctorUserId),
      displayName,
      imageUrl: doctorProfileImageUrl || null,
      slug: typeof slug === 'string' ? slug : currentSlug ? String(currentSlug) : null,
    });

    return () => {
      clearProfileDoctorInfo(epoch);
    };
  }, [information?.user_id, user_id, slug, currentSlug, progressivePending]);

  // Keep display name / avatar fresh without clearing the cached user id.
  useEffect(() => {
    if (progressivePending) return;
    const cached = getProfileDoctorInfo();
    if (!cached?.userId) return;

    const displayName =
      information?.display_name?.trim() ||
      [information?.name, information?.family].filter(Boolean).join(' ').trim() ||
      cached.displayName;

    updateProfileDoctorInfo({
      displayName,
      imageUrl: doctorProfileImageUrl || cached.imageUrl || null,
      slug: cached.slug ?? (typeof slug === 'string' ? slug : null),
    });
  }, [
    information?.display_name,
    information?.name,
    information?.family,
    doctorProfileImageUrl,
    slug,
    progressivePending,
  ]);

  useEffect(() => {
    if (progressivePending) return;
    const doctorId = information?.id ? String(information.id) : '';
    if (!doctorId) return;
    if (trackedDoctorIdRef.current === doctorId) return;

    trackedDoctorIdRef.current = doctorId;
    trackDoctorProfileViewByDoctorId(doctorId);
  }, [information?.id, currentSlug, progressivePending]);

  useEffect(() => {
    if (progressivePending) return;
    if (hasDoctorIdentity(userInfo) && slug === userInfo?.provider?.slug && information) {
      setEditable(true);
      splunkInstance('doctor-profile').sendEvent({
        group: 'profile',
        type: 'view-as',
        event: {
          action: 'page-view',
          doctor: information?.display_name ?? '',
          slug,
          terminal_id: getCookie('terminal_id'),
        },
      });
    }
  }, [userInfo, slug, progressivePending]);

  const handleViewAs = (key: 'information' | 'gallery' | 'biography' | 'services' | 'workHours') => {
    const views = {
      information: {
        title: 'ویرایش اطلاعات فردی',
        url: '/profile/info?isWebView=1&secretary_phone=off&biography=off&sticky=on',
      },
      biography: {
        title: 'ویرایش توضیحات و بیوگرافی',
        url: '/profile/biography?isWebView=1&sticky=on',
      },
      services: {
        title: 'ویرایش تخصص',
        url: '/profile/expertises?isWebView=1&sticky=on',
      },
      gallery: {
        title: 'ویرایش تصاویر گالری',
        url: '/profile/gallery?isWebView=1',
      },
      workHours: {
        title: 'ویرایش ساعت کاری',
        url: '/setting/workhours?isWebView=1&sticky=on',
      },
    };
    setViewAsData({
      ...views[key],
    });
    splunkInstance('doctor-profile').sendEvent({
      group: 'profile',
      type: 'view-as',
      event: {
        action: `click-${key}`,
        doctor: information.display_name,
        slug,
        terminal_id: getCookie('terminal_id'),
      },
    });
    handleOpenViewAsModal();
  };

  // Header rate line: keep the last shown rate (search seed, then real) while feedbacks are briefly
  // pending again — rate details arrive before the reviews list, and dropping back to the skeleton
  // in between made the rate flicker. Per doctor: never carry doctor A's rate into /dr/B.
  const lastFeedbacksRef = useRef<{ slug: string; value: any } | undefined>(undefined);
  if (feedbacks !== undefined) lastFeedbacksRef.current = { slug: String(currentSlug), value: feedbacks };
  const lastFeedbacks = lastFeedbacksRef.current?.slug === String(currentSlug) ? lastFeedbacksRef.current.value : undefined;
  const headFeedbacks = feedbacks ?? lastFeedbacks;
  const bookingHints = useMemo(
    () => (isClientShell && progressive.seed ? { online: progressive.seed.online, presence: progressive.seed.presence } : null),
    [isClientShell, progressive.seed],
  );
  const pieceRate = isClientShell ? (progressive.pieces.rateDetails as any) : undefined;
  const headRateDetails: any =
    headFeedbacks?.details ??
    (pieceRate
      ? { average_rates: pieceRate.averageRates, count_of_feedbacks: pieceRate.countOfFeedbacks, hide_rates: pieceRate.hide_rates }
      : isClientShell
        ? undefined
        : headFeedbacks?.details);

  const profileData = {
    progressivePending,
    information: {
      ...(information ?? {}),
      display_name: information?.display_name,
    },
    centers,
    expertises,
    history,
    onlineVisit,
    waitingTimeInfo,
    feedbacks,
    media,
    symptomes,
    similarLinks,
    similarDoctors,
    isBulk,
    editable,
    handleViewAs,
    customize,
    seo: { breadcrumbs, slug },
    hamdastWidgetsData,
    hamdastWidgets,
    bikoInsurance,
    centerFacilities,
    keywordTags: resolvedKeywordTags,
    user_id,
    useSamanAvailabilityStatus: ssrUseSamanAvailabilityStatus,
    profile_backend,
  };

  if (isSsrErrorPage && !shouldFetchOnClient) {
    return (
      <ErrorPage
        statusCode={Number(status)}
        message={errorMessage}
        refresh={() => window.location.reload()}
        slug={slug ?? currentSlug}
      />
    );
  }

  if (isClientShell && progressive.fatal) {
    return <ErrorPage statusCode={progressive.fatal.statusCode} refresh={() => location.reload()} slug={currentSlug} />;
  }

  const asideSections = Aside({
    ...profileData,
    fragmentComponents,
    hamdast: { ga: showHamdastGa },
  });
  const mainSections = sections({ ...profileData, fragmentComponents });
  // DOM order on mobile: head, aside, main. Only the client shell reveals progressively.
  // Reveals are final per doctor (see revealSectionStates); a new slug starts a new set.
  if (revealedSectionsRef.current.slug !== currentSlug) revealedSectionsRef.current = { slug: currentSlug, set: new Set() };
  const sectionStates = isClientShell
    ? revealSectionStates(
        ['head', ...asideSections.map((x: any) => x.sectionId), ...mainSections.map((x: any) => x.sectionId)],
        id => sectionReady(id, progressive.pieces, progressive.seed) && (!SAMAN_GATED.includes(id) || samanSettled) &&
          (id !== 'booking' || !chatLifecycleForReveal.isPending) &&
          (id === 'head' || asideChunksReady),
        revealedSectionsRef.current.set,
      )
    : undefined;
  const headPending = sectionStates?.[0] === 'pending';
  // Bumps on every reveal so both columns remount their pending skeletons (no CLS from moving them).
  const pendingKey = sectionStates?.filter(s => s === 'decided').length;
  // The main column sits under the aside on mobile; while the aside still has skeletons the main
  // column is all skeletons. Remount it once the aside is done so its box is new, not moved (CLS).
  const asideDone = !sectionStates || sectionStates.slice(0, 1 + asideSections.length).every(s => s === 'decided');

  const brandName = customize?.partnerTitle || 'پذیرش24';
  const resolvedDocumentTitle =
    (typeof documentTitleFromProps === 'string' && documentTitleFromProps.trim()) ||
    information?.display_name?.trim() ||
    '';
  const browserTitle = resolvedDocumentTitle
    ? resolvedDocumentTitle.includes('|')
      ? resolvedDocumentTitle
      : `${resolvedDocumentTitle} | ${brandName}`
    : '';

  const shouldHydrateClientSeo = layoutWasCsrFallback && !!information?.id;

  return (
    <>
      {shouldHydrateClientSeo && (
        <Seo
          title={
            (typeof documentTitleFromProps === 'string' && documentTitleFromProps.trim()) ||
            information?.display_name ||
            ''
          }
          description={(typeof documentDescriptionFromProps === 'string' && documentDescriptionFromProps) || ''}
          jsonlds={clientSeoJsonLds}
          openGraph={{
            image: {
              src: doctorProfileImageUrl,
              alt: `${information?.display_name ?? ''}`,
              type: 'image/jpg',
            },
          }}
          host={documentHostFromProps}
          noIndex={false}
        />
      )}
      {rateDebug && (
        <div
          className="fixed top-14 right-2 z-[10000] max-w-md rounded-md border border-amber-300 bg-amber-50/95 p-2 text-[11px] leading-relaxed text-amber-950 font-mono shadow-md backdrop-blur-sm"
          dir="ltr"
        >
          <div className="font-bold text-amber-900">rateDebug (add ?rateDebug=1)</div>
          <div>slug: {String(rateDebugSlug ?? '')}</div>
          <div>showRateAndReviews (customize): {String(customize?.showRateAndReviews)}</div>
          <div>ravi_show_external_rate (hides review bars): {String(dontShowRateDetails)}</div>
          <div>
            client probe /api/ravi-rate:{' '}
            {rateDebugProbe.isLoading
              ? 'loading…'
              : rateDebugProbe.isError
              ? `error: ${(rateDebugProbe.error as any)?.message ?? 'unknown'}`
              : `ok (list ${rateDebugProbe.data?.list?.length ?? 0})`}
          </div>
          <div>
            SSR props average_rates:{' '}
            {[
              feedbacks?.details?.average_rates?.average_quality_of_treatment,
              feedbacks?.details?.average_rates?.average_doctor_encounter,
              feedbacks?.details?.average_rates?.average_explanation_of_issue,
            ].every(v => v == null || v === '')
              ? 'empty (getRateDetails failed or no row)'
              : 'set'}
          </div>
          <div className="text-[10px] text-amber-800 mt-1">
            Tip: first HTML load fetches rate on the server — you will not see ravi-api in the browser Network tab until
            client refetch (e.g. profile client fetch or this probe).
          </div>
        </div>
      )}
      <Head>
        {browserTitle ? <title>{browserTitle}</title> : null}
        <link rel="dns-prefetch" href="//cdn.paziresh24.com" />
        <link rel="dns-prefetch" href="//pic.paziresh24.com" />
        <link rel="preconnect" href="https://cdn.paziresh24.com" crossOrigin="" />
        <link rel="preconnect" href="https://pic.paziresh24.com" crossOrigin="" />
        {isPicUserImageUrl(headImageUrl) ? (
          <link
            rel="preload"
            as="image"
            href={headImageUrl}
            // Avatar is ~85px CSS; tell the browser not to treat this as a full-bleed hero.
            imageSizes="85px"
            fetchPriority="high"
          />
        ) : null}
      </Head>
      <ProfileBackendDebugBadge
        enabled={showProfileBackendDebug}
        profile_backend={profile_backend}
        profile_api_id={profile_api_id}
        profile_fallback_reason={profile_fallback_reason}
        useSamanAvailabilityStatus={ssrUseSamanAvailabilityStatus}
        slug={String(slug ?? currentSlug ?? '')}
        profile_type={profile_type}
        isBulk={!!isBulk}
        shouldFetchOnClient={!!(shouldFetchOnClientFromProps ?? shouldFetchOnClient)}
        profile_perf={profile_perf}
        profile_debug_meta={profile_debug_meta}
      />
      <RaviGlobalContextsProvider>
        <div className="lg:min-w-[320px] w-full lg:max-w-[1160px] mx-auto">
          {isProfileScriptsReady &&
            hamdastWidgets
              ?.filter?.((item: any) => item?.placement?.includes?.('profile_scripts_tag') && item?.script)
              ?.map((item: any) => (
                <Script key={item?.id} id={item?.id} strategy="lazyOnload">
                  {item?.script}
                </Script>
              ))}
          <BookingHintsContext.Provider value={bookingHints}>
          <main
            key={isClientShell ? currentSlug : information?.id}
            className="lg:py-10 pwa:pb-24"
            data-progressive={isClientShell ? '' : undefined}
            data-profile-rum-slug={currentSlug}
            data-profile-content-ready={!headPending ? 'true' : 'false'}
            data-profile-interactive={!isPageLoading ? 'true' : 'false'}
          >
            <div
              className="lg:float-right lg:w-[670px] mb-3"
              data-profile-head-wrap=""
              data-avatar-pending={isClientShell && !headImageUrl ? '' : undefined}
            >
              {headPending ? (
                <div data-section-id="head">
                  <ProfileSectionSkeleton id="head" />
                </div>
              ) : (
                <ProfileHead
                  {...progressiveHeadArgs(
                    {
                      pageViewCount: profileData.history?.count_of_page_view,
                      serviceList: buildDoctorExpertiseChips(profileData.expertises?.expertises, {
                        keywordTags: profileData.keywordTags,
                      }),
                      displayName: profileData.information.display_name,
                      title: information?.experience ? `${profileData.information?.experience} سال تجربه` : undefined,
                      subTitle: `${isPsychologist(profileData.expertises) ? MEMBERSHIP_NUMBER_LABEL : 'شماره نظام پزشکی'}: ${profileData.information?.employee_id}`,
                      imageUrl: isClientShell ? headImageUrl ?? TRANSPARENT_PIXEL : doctorProfileLcpImageUrl,
                      slug: slug,
                    },
                    isClientShell ? { seed: progressive.seed, pieces: progressive.pieces } : null,
                  )}
                  widgets={hamdastWidgets
                    ?.filter((widget: any) => widget?.placement?.includes?.('head'))
                    ?.map((widget: any) => (
                      <Hamdast
                        key={widget.id}
                        id={widget.id}
                        app={widget?.app}
                        backendData={hamdastWidgetsData?.[widget.id] ?? undefined}
                        profileData={profileData}
                        widgetData={{
                          placement: widget?.placement,
                          placement_metadata: widget.placements_metadata,
                        }}
                      />
                    ))}
                  rate={
                    !shouldShowProfileHeadRateBox(customize.showRateAndReviews)
                      ? null
                      : headRateDetails === undefined
                        ? 'pending'
                        : {
                            count: headRateDetails.count_of_feedbacks,
                            rate:
                              customize.showRateAndReviews &&
                              !dontShowRateAndReviewMessage &&
                              formatAverageRate(headRateDetails.average_rates),
                            hideRates: headRateDetails.hide_rates,
                          }
                  }
                  rateArgs={profileData}
                  reserveRateHeight={isClientShell}
                />
              )}
            </div>


            <ProfileSectionTabs />

            <div className="flex flex-col space-y-3 lg:float-left lg:w-[calc(100%_-_690px)]">
              <ProfileSectionList
                sections={asideSections}
                allowFallback={false}
                states={sectionStates?.slice(1, 1 + asideSections.length)}
                pendingKey={pendingKey}
              />
            </div>

            <div key={`main-${asideDone}`} className="lg:float-right lg:w-[670px] flex flex-col space-y-3">
              <ProfileSectionList
                sections={mainSections}
                allowFallback
                states={sectionStates?.slice(1 + asideSections.length)}
                pendingKey={pendingKey}
              />
            </div>

            <div className="clear-both"></div>
          </main>
          </BookingHintsContext.Provider>
          <Modal name="profile" {...viewAsModalProps} title={viewAdData?.title ?? ''} fullScreen bodyClassName="p-0">
            <iframe
              title={viewAdData?.title || 'ویرایش پروفایل پزشک'}
              src={`${publicRuntimeConfig.DOCTOR_APP_BASE_URL}${viewAdData?.url}`}
              className="w-full h-full"
            />
          </Modal>
        </div>
      </RaviGlobalContextsProvider>
      {isPageLoading && (
        <div className="fixed top-0 left-0 w-full h-full bg-white bg-opacity-80 flex justify-center items-center z-[99999999999999999999999]">
          <Loading width={50} />
        </div>
      )}
    </>
  );
};

DoctorProfile.getLayout = function getLayout(page: ReactElement) {
  const {
    title,
    description,
    slug,
    expertises,
    centers = [],
    information,
    feedbacks,
    host,
    user_id,
    breadcrumbs,
    shouldFetchOnClient,
    noIndex: noIndexFromProps,
  } = page.props;
  const doctorProfileImageUrl = resolveDoctorProfileImageUrl(information, user_id);
  const isCsrShell = !!shouldFetchOnClient || !!noIndexFromProps;

  return (
    <LayoutWithHeaderAndFooter
      showSearchSuggestionButton={true}
      shouldShowPromoteApp={false}
      appBar={{ image: doctorProfileImageUrl || undefined }}
      {...page.props.config}
    >
      <Seo
        title={isCsrShell ? '' : title}
        description={isCsrShell ? '' : description}
        jsonlds={
          isCsrShell
            ? []
            : centers?.length > 0
            ? buildDoctorProfileJsonLds({
                slug,
                centers,
                information,
                expertises,
                feedbacks,
                breadcrumbs,
                doctorProfileImageUrl,
              })
            : []
        }
        openGraph={{
          image: {
            src: doctorProfileImageUrl,
            alt: `${information?.display_name ?? ''}`,
            type: 'image/jpg',
          },
        }}
        host={host}
        noIndex={isCsrShell}
      />
      {page}
    </LayoutWithHeaderAndFooter>
  );
};

const isSearchOrBookingUrl = (url: string) => url.startsWith('/s') || url.startsWith('/booking');

 const _getServerSideProps = getProfileServerSideProps;

export default DoctorProfile;


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/dr/[slug]',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  